import { AUTH_CONNECTION, LANGUAGE_MAP, LANGUAGES, THEME_MODES } from "@web3auth/auth";
import { type WalletUiConfig } from "@web3auth/no-modal";

import { capitalize } from "@/utils/string";

import { LoginModalConfig, WhiteLabelDataModifiedType } from "./interface";

export const USER_MODULE_KEY = "userModule";
export const PROJECT_MODULE_KEY = "projectModule";
export const VERIFIER_MODULE_KEY = "verifierModule";
export const TEAM_MODULE_KEY = "teamModule";
export const STATUS_MODULE_KEY = "statusModule";
export const NOTIFICATION_MODULE_KEY = "notificationModule";
export const BILLING_MODULE_KEY = "billingModule";
export const DOWNLOADS_MODULE_KEY = "downloadsModule";

export const STORE_MODULES = [
  USER_MODULE_KEY,
  TEAM_MODULE_KEY,
  STATUS_MODULE_KEY,
  PROJECT_MODULE_KEY,
  NOTIFICATION_MODULE_KEY,
  BILLING_MODULE_KEY,
  DOWNLOADS_MODULE_KEY,
];

export type UserProfile = {
  ID?: number;
  name?: string;
  project_name?: string;
  email?: string;
  role?: string;
  job_role?: string;
  country?: string;
  accepted_terms_and_privacy_policy?: boolean;
  verifier?: string;
  verifier_id?: string;
};

export type ToastVariants = "default" | "success" | "danger" | "info";

export type ToastMessage = {
  type?: ToastVariants;
  message: string;
};

export const TOAST_TYPES: { [key: string]: ToastVariants } = {
  SUCCESS: "success",
  ERROR: "danger",
  INFO: "info",
};

export type Snackbar = {
  show: boolean;
  message: string;
  color: string;
};

export type NotificationDetails = {
  ID: number;
  description: string;
  details: string;
  live_at: string;
  title: string;
  type: string;
  is_read: boolean;
};

// networks
export const MAINNET = "mainnet";
export const TESTNET = "testnet";
export const GOERLI = "goerli";
export const CYAN = "cyan";
export const AQUA = "aqua";
export const CELESTE = "celeste";
export const SAPPHIRE_DEVNET = "sapphire_devnet";
// export const SAPPHIRE_TESTNET = "sapphire_testnet";
export const SAPPHIRE_MAINNET = "sapphire_mainnet";

// verifier types (saved in dashboard db)
export const GOOGLE_VERIFIER = "google";
export const FACEBOOK_VERIFIER = "facebook";
export const TWITCH_VERIFIER = "twitch";
export const DISCORD_VERIFIER = "discord";
export const AUTH0_VERIFIER = "auth0";
export const FIREBASE_VERIFIER = "firebase";
export const COGNITO_VERIFIER = "cognito";
export const EMAIL_PASSWORDLESS_VERIFIER = "email_passwordless";
export const SMS_PASSWORDLESS_VERIFIER = "sms_passwordless";
export const SINGLE_ID_VERIFIER = "single_id_verifier";
export const CUSTOM_VERIFIER = "custom";

export const VERIFIER = {
  GOOGLE: GOOGLE_VERIFIER,
  FACEBOOK: FACEBOOK_VERIFIER,
  TWITCH: TWITCH_VERIFIER,
  DISCORD: DISCORD_VERIFIER,
  AUTH0: AUTH0_VERIFIER,
  FIREBASE: FIREBASE_VERIFIER,
  COGNITO: COGNITO_VERIFIER,
  EMAIL_PASSWORDLESS: EMAIL_PASSWORDLESS_VERIFIER,
  SMS_PASSWORDLESS: SMS_PASSWORDLESS_VERIFIER,
  SINGLE_ID: SINGLE_ID_VERIFIER,
  CUSTOM: CUSTOM_VERIFIER,
} as const;

export type VerifierType = (typeof VERIFIER)[keyof typeof VERIFIER];

export const SUB_VERIFIER_TYPE_JWT = "jwt";

// Auth0 Social Connection
export const GOOGLE = "google";
export const APPLE = "apple";
export const GITHUB = "github";
export const KAKAO = "kakao";
export const LINE = "line";
export const LINKEDIN = "linkedin";
export const TWITTER = "twitter";
export const WEIBO = "weibo";
export const WECHAT = "wechat";
export const CUSTOM = "custom";
export const OTHER = "other";
export const REDDIT = "reddit";

// Auth0 Passwordless
export const EMAIL_PASSWORD = "email-password";
export const PASSWORDLESS = "passwordless";

// error messages
export const VERIFIER_ID_TAKEN = "This verifier identifier has already been taken. Please try again. ";
export const VERIFIER_NOT_UNIQUE = "Verifier already exists on the network.";
export const MISSING_AUD = "Missing aud in JWT Validation.";
export const VERIFIER_ASSIGNED = "Verifier has already been transferred.";

// verifier states
export const LIVE = "Live";
export const PROCESSING = "Processing";
export const FAILED = "Failed";
export const UPDATING = "Updating";
export const UPDATED = "Updated";
export const UPDATE_FAILED = "Update Failed";
export const TRANSFERRED = "Transferred";
export const TRANSFERRING = "Transferring";
export const TRANSFER_FAILED = "Transfer Failed";
export const DEPLOYED = "Deployed";
export const REJECTED = "Rejected";

// verifier actions
export const CREATE = "create";
export const UPDATE = "update";
export const TRANSFER = "transfer";

// project status
export const PROJECT_STATUS: Record<string, string> = {
  live: "Live",
  in_development: "In development",
};

// user types
export const ADMIN = "admin";
export const DEVELOPER = "developer";

// Invoice status https://stripe.com/docs/invoicing/overview#invoice-statuses
export const STRIPE_INVOICE_STATUS = {
  draft: "draft",
  open: "open",
  paid: "paid",
  uncollectible: "uncollectible",
  void: "void",
};

// Payment Status
export const PAYMENT_STATUS = {
  unpaid: "unpaid",
  paid: "paid",
  past_due: "past_due",
  failed: "failed",
};

export const INVOICE_STATUS = {
  paid: "paid",
  unpaid: "unpaid",
  waived: "waived",
  overdue: "overdue",
};

// Invoice type
export const INVOICE_TYPE_MONTHLY = "monthly";
export const INVOICE_TYPE_RETROACTIVE = "retroactive";

// Subscription status
export const SUBSCRIPTION_STATUS_ACTIVE = "active";
export const SUBSCRIPTION_STATUS_PENDING = "pending";
export const SUBSCRIPTION_STATUS_PASTDUE = "past_due";
export const SUBSCRIPTION_STATUS_UNPAID = "unpaid";

// Subscription plan
export const SUBSCRIPTION_PLAN = {
  base: "base",
  growth: "growth",
  scale: "scale",
  enterprise: "enterprise",
};

export const PLAN_TYPE_TEAM_SEATS_MAP = {
  [SUBSCRIPTION_PLAN.base]: 1,
  [SUBSCRIPTION_PLAN.growth]: 4,
  [SUBSCRIPTION_PLAN.scale]: 8,
};

export type INetwork = {
  id: string;
  name: string;
  value: string;
  notice: string;
  description: string;
};

export const NETWORKS: INetwork[] = [
  {
    id: SAPPHIRE_DEVNET,
    name: "Sapphire Devnet",
    value: SAPPHIRE_DEVNET,
    notice: "Low latencies globally, ideal for all apps",
    description: `A development environment for configuring and testing your integration. Devnet undergoes periodic key rotations that may lead to lost wallets.`,
  },
  {
    id: SAPPHIRE_MAINNET,
    name: "Sapphire Mainnet",
    value: SAPPHIRE_MAINNET,
    notice: "Low latencies globally, ideal for all apps",
    description: `A high-performance, scalable platform for low-latency global launches. Payment info is required, but charges apply only if you exceed the free MAW limit or choose a paid plan.`,
  },
  {
    id: CYAN,
    name: "Legacy Cyan Mainnet (USA)",
    value: CYAN,
    notice: "Ideal for apps near the USA",
    description: `Legacy Cyan Mainnet has been deprecated and all users have been migrated to Sapphire Mainnet.`,
  },
  {
    id: AQUA,
    name: "Legacy Aqua Mainnet (Asia)",
    value: AQUA,
    notice: "Ideal for apps near Asia",
    description: `Legacy Aqua Mainnet has been deprecated and all users have been migrated to Sapphire Mainnet.`,
  },
  {
    id: MAINNET,
    name: "Legacy Mainnet",
    value: MAINNET,
    notice: "Ideal for apps near Asia",
    description: `Legacy Mainnet has been deprecated and all users have been migrated to Sapphire Mainnet.`,
  },
  {
    id: TESTNET,
    name: "Legacy Testnet",
    value: TESTNET,
    notice: "Speed is slower on Testnet",
    description: `Legacy Testnet has been deprecated and all users have been migrated to Sapphire Devnet.`,
  },
  {
    id: CELESTE,
    name: "Celeste Mainnet (Korea)",
    value: CELESTE,
    notice: "Ideal for apps near Korea",
    description: `Ideal for dApps near Korea and expecting >10,000 MAUs.`,
  },
];

export const VERIFIER_TYPES_SOCIAL_LOGIN = [
  {
    name: "Google",
    value: GOOGLE_VERIFIER,
  },
  {
    name: "Facebook",
    value: FACEBOOK_VERIFIER,
  },
  {
    name: "Twitch",
    value: TWITCH_VERIFIER,
  },
  {
    name: "Discord",
    value: DISCORD_VERIFIER,
  },
  {
    name: "Auth0",
    value: AUTH0_VERIFIER,
  },
  {
    name: "Email Passwordless",
    value: EMAIL_PASSWORDLESS_VERIFIER,
  },
  {
    name: "SMS Passwordless",
    value: SMS_PASSWORDLESS_VERIFIER,
  },
];

export const TESTNET_NETWORKS = NETWORKS.filter((network) => [TESTNET, SAPPHIRE_DEVNET].includes(network.id));
export const SAPPHIRE_TESTNET_NETWORKS = NETWORKS.filter((network) => [SAPPHIRE_DEVNET].includes(network.id));
export const LEGACY_MAINNET_NETWORKS = NETWORKS.filter((network) => [MAINNET, CYAN, AQUA].includes(network.id));
export const SAPPHIRE_MAINNET_NETWORKS = NETWORKS.filter((network) => [SAPPHIRE_MAINNET].includes(network.id));
export const SAPPHIRE_NETWORKS = NETWORKS.filter((network) => [SAPPHIRE_DEVNET, SAPPHIRE_MAINNET].includes(network.id));
export const MAINNET_NETWORKS = [...LEGACY_MAINNET_NETWORKS, ...SAPPHIRE_MAINNET_NETWORKS];

export const JWT_VERIFIERS = [AUTH0_VERIFIER, CUSTOM_VERIFIER];

export const AUTH0_AUTHENTICATION_TYPES = [
  {
    name: "Google",
    value: GOOGLE,
  },
  {
    name: "Twitter",
    value: TWITTER,
  },
  {
    name: "Reddit",
    value: REDDIT,
  },
  {
    name: "Line",
    value: LINE,
  },
  {
    name: "Apple",
    value: APPLE,
  },
  {
    name: "Github",
    value: GITHUB,
  },
  {
    name: "LinkedIn",
    value: LINKEDIN,
  },
  {
    name: "WeChat",
    value: WECHAT,
  },
  {
    name: "Weibo",
    value: WEIBO,
  },
  {
    name: "KaKao",
    value: KAKAO,
  },
  {
    name: "Email Passwordless",
    value: PASSWORDLESS,
  },
  {
    name: "Email Password",
    value: EMAIL_PASSWORD,
  },
  {
    name: "Custom",
    value: CUSTOM,
  },
  {
    name: "Other",
    value: OTHER,
  },
];

export const OP_COMPLETE_STATES = [LIVE, FAILED, UPDATE_FAILED, TRANSFER_FAILED];

export const VERIFIER_ERROR_MESSAGES = [
  {
    message: "verifier_id already exists",
    value: VERIFIER_ID_TAKEN,
  },
  {
    message: "verifier already assigned",
    value: VERIFIER_ASSIGNED,
  },
  {
    message: "verifier already exists on the network",
    value: VERIFIER_NOT_UNIQUE,
  },
];

export const DAPP_CHAIN_NAMESPACES = ["evm", "solana", "others"];

export const SETTINGS_TABS = {
  general: "General",
  member: "Member",
};

export const BILLING_TABS = {
  plans: "Plans & Subscription",
  payment: "Payment",
};

export const ROLE = {
  owner: "owner",
  admin: "admin",
  billing: "billing",
  member: "member",
};

export const ROLE_DESCRIPTION = {
  [ROLE.owner]: "Able to view and manage project settings, organization, billing and delete the organization.",
  [ROLE.admin]: "Able to view and manage project settings, organization, billing in the organization.",
  [ROLE.member]: "Able to only view projects in your organization.",
  [ROLE.billing]: "Able to only view projects and manage the billing settings of your organization.",
};

export enum TEAM_PERMISSION {
  owner = "owner",
  edit = "edit",
  billing = "billing",
  view = "view",
}

export const ROLE_PERMISSION_MAPPING = {
  [ROLE.owner]: [TEAM_PERMISSION.owner, TEAM_PERMISSION.edit, TEAM_PERMISSION.billing, TEAM_PERMISSION.view],
  [ROLE.admin]: [TEAM_PERMISSION.edit, TEAM_PERMISSION.billing, TEAM_PERMISSION.view],
  [ROLE.billing]: [TEAM_PERMISSION.billing, TEAM_PERMISSION.view],
  [ROLE.member]: [TEAM_PERMISSION.view],
};

export const SUPPORTED_CARD_BRANDS = {
  amex: "Amex",
  diners: "Diners",
  discover: "Discover",
  jcb: "Jcb",
  mastercard: "Mastercard",
  unionpay: "Unionpay",
  visa: "Visa",
};

export const WEB3AUTH_BILLING_EMAIL = "billing@metamask.io";

export const VERIFIER_SOCIAL_LOGIN_TYPE_MAP: Record<string, string> = {
  "tkey-auth0-email-passwordless": "email_passwordless",
  "tkey-google": "google",
};

export const ORGANIZATION_INDUSTRIES = ["Gaming", "Finance", "NFT", "Loyalty", "Consulting/ Services", "Dev Infra/ Tooling", "Social", "Others"];

export const ORGANIZATION_SIZES = ["1-9", "10-49", "50-249", "250-999", "More than 1,000"];

export const PLATFORM_WEB = "web";
export const PLATFORM_REACT_NATIVE = "react native";
export const PLATFORM_FLUTTER = "flutter";
export const PLATFORM_IOS = "ios";
export const PLATFORM_ANDROID = "android";
export const PLATFORM_UNITY = "unity";
export const PLATFORM_UNREAL = "unreal";

export const PLATFORM_TYPES = [
  {
    name: "Web Application",
    value: PLATFORM_WEB,
  },
  {
    name: "React Native",
    value: PLATFORM_REACT_NATIVE,
  },
  {
    name: "Flutter",
    value: PLATFORM_FLUTTER,
  },
  {
    name: "iOS",
    value: PLATFORM_IOS,
  },
  {
    name: "Android",
    value: PLATFORM_ANDROID,
  },
  {
    name: "Unity",
    value: PLATFORM_UNITY,
  },
  {
    name: "Unreal",
    value: PLATFORM_UNREAL,
  },
];

export const PROFESSIONS = [
  { value: "developer", name: "Developer" },
  { value: "marketing", name: "Marketing" },
  { value: "business-relation", name: "Business relations" },
  { value: "developer-relations", name: "Developer relations" },
  { value: "projet-manager", name: "Project manager" },
  { value: "product-manager", name: "Product manager" },
  { value: "product-designer", name: "Product designer" },
  { value: "founder", name: "Founder" },
  { value: "others", name: "Others" },
];

// TODO: update value to core kit mpc
export const PRODUCT_CORE_KIT = "core kit";
export const PRODUCT_CORE_KIT_SSS = "core kit sss";
export const PRODUCT_PLUG_AND_PLAY = "pnp";
export const PRODUCT_INFRA_SDKS = "infra";
export const PRODUCT_SAFEAUTH = "safe auth";

export const PRODUCTS = [
  {
    name: "Plug and Play",
    value: PRODUCT_PLUG_AND_PLAY,
  },
  {
    name: "MPC Core Kit",
    value: PRODUCT_CORE_KIT,
  },
  {
    name: "Single Factor Auth",
    value: PRODUCT_CORE_KIT_SSS,
  },
  {
    name: "Infra SDKs",
    value: PRODUCT_INFRA_SDKS,
  },
  {
    name: "SafeAuth",
    value: PRODUCT_SAFEAUTH,
  },
];

export const LOCAL_STORAGE = {
  lastVisitedOrganization: "last_visited_organization",
  lastAgenticOrganization: "last_agentic_organization",
  gettingStartedHidden: "getting_started_hidden",
  sidebarDevnetNoticeHidden: "sidebar_devnet_notice_hidden",
  infuraNetworkNoticeHidden: "infura_network_notice_hidden",
};

export const WEB3AUTH_PASSWORDLESS_VERIFIER_JWKS_URL_MAINNET = "https://api-passwordless.web3auth.io/api/v3/auth/cert";

export const WEB3AUTH_PASSWORDLESS_VERIFIER_JWKS_URL_DEVNET = "https://api-develop-passwordless.web3auth.io/api/v3/auth/cert";

export const ROUTES = {
  LOGIN: "login",
  LOGIN_EMAIL: "login-email",
  START: "start",
  END: "end",
  CREATE_PROFILE: "create-profile",
  UPDATE_PROFILE: "update-profile",
  CREATE_ORGANIZATION: "create-organization",
  INVITATION: "invitation",
  ACCEPT_INVITE: "accept-invite",
  HOME: "home",
  PROFILE_SETTINGS: "profile-settings",
  GETTING_STARTED: "getting-started",
  TEAM_PROJECTS: "team-projects",
  TEAM_ARCHIVE: "team-archive",
  TEAM_VERIFIERS: "team-verifiers",
  TEAM_VERIFIER_DETAILS: "team-verifier-details",
  TEAM_SETTINGS: "team-settings",
  TEAM_SETTINGS_GENERAL: "team-settings-general",
  TEAM_SETTINGS_MEMBERS: "team-settings-members",
  TEAM_BILLING: "team-billing",
  TEAM_BILLING_PLAN_AND_SUBSCRIPTION: "team-billing-plan-and-subscription",
  TEAM_BILLING_PAYMENT: "team-billing-payment",
  TEAM_PROJECT: "team-project",
  TEAM_PROJECT_SETTINGS: "team-project-settings",
  TEAM_PROJECT_SETTINGS_GENERAL: "team-project-settings-general",
  TEAM_PROJECT_SETTINGS_DOMAINS: "team-project-settings-domains",
  TEAM_PROJECT_ACCESS_CONTROL: "team-project-access-control",
  TEAM_PROJECT_SETTINGS_ADVANCED: "team-project-settings-advanced",
  TEAM_PROJECT_ANALYTICS: "team-project-analytics",
  TEAM_PROJECT_CHAIN_NETWORKS: "team-project-chain-networks",
  TEAM_PROJECT_AUTHENTICATION: "team-project-authentication",
  TEAM_PROJECT_SOCIAL_CONNECTIONS: "team-project-social-connections",
  TEAM_PROJECT_CUSTOM_CONNECTIONS: "team-project-custom-connections",
  TEAM_PROJECT_GROUP_CONNECTIONS: "team-project-group-connections",
  TEAM_PROJECT_ARCHIVED_CONNECTIONS: "team-project-archived-connections",
  TEAM_PROJECT_SMART_ACCOUNTS: "team-project-smart-accounts",
  TEAM_PROJECT_SMART_ACCOUNTS_GENERAL: "team-project-smart-accounts-general",
  TEAM_PROJECT_SMART_ACCOUNTS_CHAINS_NETWORKS: "team-project-smart-accounts-chains-networks",
  TEAM_PROJECT_LOGIN_MODAL: "team-project-login-modal",
  TEAM_PROJECT_BRANDING: "team-project-branding",
  TEAM_PROJECT_WALLET_CONFIGURATION: "team-project-wallet-configuration",
  TEAM_PROJECT_WALLET_FUNDING: "team-project-wallet-funding",
  TEAM_PROJECT_USERS: "team-project-users",
  TEAM_CUSTOM_AUTH_LIST: "team-custom-auth-list",
  TEAM_CUSTOM_AUTH_DETAILS: "team-custom-auth-details",
  TEAM_CUSTOM_AUTH_NEW: "team-custom-auth-new",
  // TEAM_CUSTOMIZE: "team-customize",
  // TEAM_ANALYTICS: "team-analytics",
  AGENTIC_AUTH: "agentic-auth",
  TEAM_ADDONS: "team-addons",
  AGENTIC_APPROVAL: "agentic-approval",
  /** Agentic workspace */
  AGENTIC_LOGIN: "agentic-login",
  AGENTIC_ENTRY: "agentic-entry",
  AGENTIC_ORGANIZATION: "agentic-organization",
  AGENTIC_PROJECTS: "agentic-projects",
  AGENTIC_PROJECT_DETAILS: "agentic-project-details",
};

export const ALLOW_FEW_MINUTES_FOR_UPDATE_MSG = "Please allow up to 5 minutes for the code to fully reflect the changes.";

export const SOCIAL_CONNECTION = {
  google: AUTH_CONNECTION.GOOGLE,
  twitter: AUTH_CONNECTION.TWITTER,
  facebook: AUTH_CONNECTION.FACEBOOK,
  discord: AUTH_CONNECTION.DISCORD,
  farcaster: AUTH_CONNECTION.FARCASTER,
  apple: AUTH_CONNECTION.APPLE,
  github: AUTH_CONNECTION.GITHUB,
  reddit: AUTH_CONNECTION.REDDIT,
  line: AUTH_CONNECTION.LINE,
  kakao: AUTH_CONNECTION.KAKAO,
  linkedin: AUTH_CONNECTION.LINKEDIN,
  twitch: AUTH_CONNECTION.TWITCH,
  wechat: AUTH_CONNECTION.WECHAT,
} as const;
export type SocialConnection = (typeof SOCIAL_CONNECTION)[keyof typeof SOCIAL_CONNECTION];

export const SOCIAL_CONNECTIONS_SUPPORTING_CUSTOM_AUTH: SocialConnection[] = [
  SOCIAL_CONNECTION.google,
  SOCIAL_CONNECTION.facebook,
  SOCIAL_CONNECTION.twitch,
  SOCIAL_CONNECTION.discord,
];

export type SocialLoginConfig = Record<
  SocialConnection,
  {
    name: SocialConnection;
    enabled: boolean;
  }
>;

export const DEFAULT_SOCIAL_LOGIN_CONFIG: SocialLoginConfig = Object.fromEntries(
  Object.values(SOCIAL_CONNECTION).map((socialConnection) => [socialConnection, { name: socialConnection, enabled: true }])
) as SocialLoginConfig;

export const CUSTOM_PAGE: { [key: string]: string } = {
  LOGIN_SCREENS: "Login Screens",
  MFA_SCREEN: "Multi-Factor Screen",
  WALLET_UI: "Wallet UI",
};

export const LANGUAGE_OPTIONS = Object.entries(LANGUAGE_MAP).map(([value, name]) => ({
  value,
  name: capitalize(name),
}));

export const THEME_OPTIONS = [
  { name: "Light Mode", value: THEME_MODES.light },
  { name: "Dark Mode", value: THEME_MODES.dark },
  { name: "Auto", value: THEME_MODES.auto },
];

export const DEFAULT_APP_NAME = "Web3Auth";
export const DEFAULT_THEME = THEME_MODES.dark;
export const DEFAULT_LANGUAGE = LANGUAGES.en;
export const DEFAULT_PRIMARY_COLOR = "#0364FF";
export const DEFAULT_ON_PRIMARY_COLOR = "#FFFFFF";
export const DEFAULT_WEB3AUTH_LOGO_LIGHT_URL =
  "https://images.ctfassets.net/clixtyxoaeas/4ES1xXFPTzqLsOumTgHcMd/e5bcf8648eeea657850731684ee4942b/MetaMask-icon-fox-developer.svg";
export const DEFAULT_WEB3AUTH_LOGO_DARK_URL =
  "https://images.ctfassets.net/clixtyxoaeas/4ES1xXFPTzqLsOumTgHcMd/e5bcf8648eeea657850731684ee4942b/MetaMask-icon-fox-developer.svg";
export const DEFAULT_WEB3AUTH_BY_METAMASK_LOGO_LIGHT_URL =
  "https://images.ctfassets.net/clixtyxoaeas/2ByJgKF9OlipCRQidv4j4E/35ac65d90a989c327a219872b9c12277/MetaMask-logo-developer-black.svg";
export const DEFAULT_WEB3AUTH_BY_METAMASK_LOGO =
  "https://images.ctfassets.net/clixtyxoaeas/2ByJgKF9OlipCRQidv4j4E/35ac65d90a989c327a219872b9c12277/MetaMask-logo-developer-black.svg";
export const AGENTIC_METAMASK_LOGO =
  "https://images.ctfassets.net/clixtyxoaeas/5LtyTxPwTw6le2QewukViF/e2b0dc242a2968cc1a557801f0481585/MetaMask-logo-black.svg";
export const BRANDING_WEB3AUTH_BY_METAMASK_LOGO_LIGHT_URL = "https://images.web3auth.io/w3a-mm-light-logo.png";
export const BRANDING_WEB3AUTH_BY_METAMASK_LOGO = "https://images.web3auth.io/login-modal/logo-light.svg";
export const BRANDING_POWERED_BY_METAMASK_FOOTER_LIGHT_LOGO = "https://images.web3auth.io/metamask-footer-logo-light.svg";
export const BRANDING_POWERED_BY_METAMASK_FOOTER_DARK_LOGO = "https://images.web3auth.io/metamask-footer-logo-dark.svg";

export const DEFAULT_WHITELABEL_CONFIG: WhiteLabelDataModifiedType = {
  appName: "Web3Auth",
  mode: DEFAULT_THEME,
  defaultLanguage: DEFAULT_LANGUAGE,
  logoLight: DEFAULT_WEB3AUTH_LOGO_LIGHT_URL,
  logoDark: DEFAULT_WEB3AUTH_LOGO_DARK_URL,
  useLogoLoader: false,
  tncLink: "",
  privacyPolicy: "",
  consentRequired: false,
  theme: {
    primary: DEFAULT_PRIMARY_COLOR,
    onPrimary: DEFAULT_ON_PRIMARY_COLOR,
  },
};

export const PROJECT_SAVE_MESSAGE = `Changes saved successfully! ${ALLOW_FEW_MINUTES_FOR_UPDATE_MSG}`;

export const DEFAULT_LOGIN_MODAL_CONFIG: LoginModalConfig = {
  widgetType: "modal",
  logoAlignment: "center",
  borderRadiusType: "large",
  buttonRadiusType: "pill",
  signInMethods: ["social", "passwordless", "externalWallets"],
  addPreviousLoginHint: false,
  displayInstalledExternalWallets: true,
  displayExternalWalletsCount: true,
};

export const DEFAULT_WALLET_UI_CONFIG: WalletUiConfig = {
  enablePortfolioWidget: true,
  enableConfirmationModal: true,
  enableWalletConnect: true,
  enableTokenDisplay: true,
  enableNftDisplay: true,
  enableDefiPositionsDisplay: true,
  enableShowAllTokensButton: true,
  enableBuyButton: true,
  enableSendButton: true,
  enableSwapButton: true,
  enableReceiveButton: true,
  portfolioWidgetPosition: "bottom-left",
  defaultPortfolio: "token",
};

export const VERIFIER_ID_HELPER_TEXT = "Use only letters, numbers, and hyphens. Must be unique, lowercase, and cannot start or end with a hyphen.";
export const SPECIFY_VERIFIER_ID_HELPER_TEXT = "Specify this ID in your login’s unique identifier within your code.";

export const QUERY_PARAMS = {
  IS_NEW_USER: "is-new-user",
};

// The actual release date for v10 changes based on PR (https://github.com/torusresearch/developer-dashboard/pull/829/) merge timing is 22nd May 2025 12:30 pm UTC
// Due to caching the date may be later so we just set an approximate cutoff date
export const V9_V10_VERIFIERS_CUTOFF_DATE = "2025-05-23";

export const CASE_SENSITIVE_USER_IDENTIFIER_DEFAULT_VALUE = true;

export const SUBSCRIPTION_PLAN_INFO = Object.freeze({
  [SUBSCRIPTION_PLAN.base]: {
    order: 1,
    name: "Base",
    monthlyPrice: "0",
    yearlyPrice: "0",
    description: "For individuals on personal workspaces",
    items: [
      {
        icon: "credit-card-solid-icon",
        text: `
        <div>Free 1,000 Monthly Active Wallets (MAWs)</div>
        <br />
        <div>+$0.050 per additional MAW*</div>
      `,
      },
      { icon: "fire-solid-icon", text: "Access to built-in social login providers" },
      { icon: "lock-closed-solid-icon", text: "Multi Factor Authentication" },
      { icon: "color-swatch-solid-icon", text: "Customizable modal" },
      { icon: "user-group-solid-icon", text: "1 Team seat" },
      { icon: "chat-alt-solid-icon", text: "Community support" },
    ],
  },
  [SUBSCRIPTION_PLAN.growth]: {
    order: 2,
    name: "Growth",
    monthlyPrice: "69",
    yearlyPrice: "662",
    postfixTitle: "/month",
    description: "For teams that require basic white-labelling and custom logins",
    items: [
      {
        icon: "credit-card-solid-icon",
        text: `
        <div>Free 3,000 Monthly Active Wallets (MAWs)</div>
        <br />
        <div>+$0.045 per additional MAW*</div>
      `,
      },
      { icon: "fire-solid-icon", text: "Customizable social login providers + your own auth (JWTs)" },
      { icon: "lock-closed-solid-icon", text: "Multi Factor Authentication" },
      { icon: "color-swatch-solid-icon", text: "Customizable modal, redirect & login screens" },
      { icon: "user-group-solid-icon", text: "4 Team seats" },
      { icon: "chat-alt-solid-icon", text: "Community support" },
    ],
  },
  [SUBSCRIPTION_PLAN.scale]: {
    order: 3,
    name: "Scale",
    monthlyPrice: "399",
    yearlyPrice: "3830",
    description: "For teams that require higher levels of customization and support",
    items: [
      {
        icon: "credit-card-solid-icon",
        text: `
        <div>Free 10,000 Monthly Active Wallets (MAWs)</div>
        <br />
        <div>+$0.040 per additional MAW*</div>
      `,
      },
      { icon: "fire-solid-icon", text: "Customizable social login providers + your own auth (JWTs)" },
      { icon: "lock-closed-solid-icon", text: "Customizable Multi-Factor Authentication" },
      { icon: "color-swatch-solid-icon", text: "Fully customizable login experience (CoreKit MFA)" },
      { icon: "wallet-solid-icon-gradient", text: "Wallet Services" },
      { icon: "wallet-solid-icon", text: "Pregenerated Wallets" },
      { icon: "user-group-solid-icon", text: "8 Team seats" },
      { icon: "chat-alt-solid-icon", text: "Enhanced Community support" },
    ],
  },
  [SUBSCRIPTION_PLAN.enterprise]: {
    order: 4,
    name: "Enterprise",
    title: "Custom",
    description: "For teams and companies with complex requirements and support",
    items: [
      { icon: "credit-card-solid-icon", text: "Volume-based MAW discounts" },
      { icon: "sparkles-solid-icon", text: "Ready-to-launch white label wallet" },
      { icon: "fire-solid-icon", text: "Access to advanced customized auth (custom domain, SMTP) + SSO" },
      { icon: "lock-closed-solid-icon", text: "Advanced MFA methods" },
      { icon: "color-swatch-solid-icon", text: "Fully customizable login experience" },
      { icon: "wallet-solid-icon-gradient", text: "Wallet Services" },
      { icon: "wallet-solid-icon", text: "Pregenerated Wallets" },
      { icon: "user-group-solid-icon", text: "Unlimited team seats" },
      { icon: "clock-solid-icon", text: "Uptime SLA commitment" },
      { icon: "chat-alt-solid-icon", text: "Dedicated technical support + committed response times" },
    ],
  },
});

// these are the types of logins that we support on w3a dashboard
// the difference between LOGIN_TYPE and AUTH_CONNECTION is that
// AUTH_CONNECTION includes values like `custom` such as in the case
// of mm <-> w3a group connection
// Regardless of how the user logged in
// we map all these users' login types to the below values only
export const LOGIN_TYPE = {
  google: AUTH_CONNECTION.GOOGLE,
  twitter: AUTH_CONNECTION.TWITTER,
  discord: AUTH_CONNECTION.DISCORD,
  github: AUTH_CONNECTION.GITHUB,
  facebook: AUTH_CONNECTION.FACEBOOK,
  apple: AUTH_CONNECTION.APPLE,
  email_passwordless: AUTH_CONNECTION.EMAIL_PASSWORDLESS,
} as const;

export const GROUPED_AUTH_CONNECTION_ID_LOGIN_TYPE_MAP: Record<string, keyof typeof LOGIN_TYPE> = {
  // localhost & test-dashboard auth connection ids and their mapping
  "web3auth-google-sapphire-devnet": LOGIN_TYPE.google,
  "web3auth-auth0-twitter-sapphire-devnet": LOGIN_TYPE.twitter,
  "web3auth-discord-sapphire-devnet": LOGIN_TYPE.discord,
  "web3auth-auth0-github-sapphire-devnet": LOGIN_TYPE.github,
  "web3auth-facebook-sapphire-devnet": LOGIN_TYPE.facebook,
  "web3auth-auth0-apple-sapphire-devnet": LOGIN_TYPE.apple,
  "web3auth-auth0-email-passwordless-sapphire-devnet": LOGIN_TYPE.email_passwordless,
  // dev-dashboard & dashboard(prod) auth connection ids and their mapping
  "tkey-google": LOGIN_TYPE.google,
  "tkey-auth0-twitter": LOGIN_TYPE.twitter,
  "tkey-discord": LOGIN_TYPE.discord,
  "tkey-auth0-github": LOGIN_TYPE.github,
  "tkey-facebook": LOGIN_TYPE.facebook,
  "tkey-auth0-apple": LOGIN_TYPE.apple,
  "tkey-auth0-email-passwordless": LOGIN_TYPE.email_passwordless,
  // Auth0 tenant (dashboard direct login)
  "auth0-google": LOGIN_TYPE.google,
  "auth0-twitter": LOGIN_TYPE.twitter,
  "auth0-github": LOGIN_TYPE.github,
  "auth0-apple": LOGIN_TYPE.apple,
  "auth0-email-passwordless": LOGIN_TYPE.email_passwordless,
} as const;

export const ETHEREUM_MAINNET_CHAIN_ID = "0x1";
export const ETHEREUM_SEPOLIA_CHAIN_ID = "0xaa36a7";
export const ETHEREUM_HOLESKY_CHAIN_ID = "0x4268";

export const POLYGON_MAINNET_CHAIN_ID = "0x89";
export const POLYGON_AMOY_TESTNET_CHAIN_ID = "0x13882";

export const BSC_MAINNET_CHAIN_ID = "0x38";
export const BSC_TESTNET_CHAIN_ID = "0x61";

export const AVALANCHE_MAINNET_CHAIN_ID = "0xa86a";
export const AVALANCHE_FUJI_TESTNET_CHAIN_ID = "0xa869";

export const FANTOM_MAINNET_CHAIN_ID = "0xfa";
export const FANTOM_TESTNET_CHAIN_ID = "0xfa2";

export const CRONOS_MAINNET_CHAIN_ID = "0x19";
export const CRONOS_TESTNET_CHAIN_ID = "0x152";

export const ARBITRUM_ONE_MAINNET_CHAIN_ID = "0xa4b1";
export const ARBITRUM_NOVA_MAINNET_CHAIN_ID = "0xa4ba";
export const ARBITRUM_SEPOLIA_TESTNET_CHAIN_ID = "0x66eee";

export const OPTIMISM_MAINNET_CHAIN_ID = "0xa";
export const OPTIMISM_SEPOLIA_TESTNET_CHAIN_ID = "0xaa37dc";

export const BASE_MAINNET_CHAIN_ID = "0x2105";
export const BASE_SEPOLIA_TESTNET_CHAIN_ID = "0x14a34";

export const LINEA_MAINNET_CHAIN_ID = "0xe708";
export const LINEA_SEPOLIA_TESTNET_CHAIN_ID = "0xe705";

export const SCROLL_MAINNET_CHAIN_ID = "0x82750";
export const SCROLL_SEPOLIA_TESTNET_CHAIN_ID = "0x2106";

export const ZKSYNC_ERA_MAINNET_CHAIN_ID = "0x144";
export const ZKSYNC_ERA_SEPOLIA_TESTNET_CHAIN_ID = "0x12c";

export const POLYGON_ZKEVM_MAINNET_CHAIN_ID = "0x44d";
export const POLYGON_ZKEVM_CARDONA_TESTNET_CHAIN_ID = "0x98a";

export const CELO_MAINNET_CHAIN_ID = "0xa4ec";
export const CELO_ALFAJORES_TESTNET_CHAIN_ID = "0xaef3";

export const GNOSIS_MAINNET_CHAIN_ID = "0x64";
export const GNOSIS_CHIADO_TESTNET_CHAIN_ID = "0x27d8";

export const MANTLE_MAINNET_CHAIN_ID = "0x1388";
export const MANTLE_SEPOLIA_TESTNET_CHAIN_ID = "0x138b";

export const BLAST_MAINNET_CHAIN_ID = "0x13e31";
export const BLAST_SEPOLIA_TESTNET_CHAIN_ID = "0xa0c71fd";

export const POPULAR_EVM_CHAINS = [
  POLYGON_MAINNET_CHAIN_ID,
  POLYGON_AMOY_TESTNET_CHAIN_ID,
  BSC_MAINNET_CHAIN_ID,
  BSC_TESTNET_CHAIN_ID,
  AVALANCHE_MAINNET_CHAIN_ID,
  AVALANCHE_FUJI_TESTNET_CHAIN_ID,
  FANTOM_MAINNET_CHAIN_ID,
  FANTOM_TESTNET_CHAIN_ID,
  CRONOS_MAINNET_CHAIN_ID,
  CRONOS_TESTNET_CHAIN_ID,
  ARBITRUM_ONE_MAINNET_CHAIN_ID,
  ARBITRUM_NOVA_MAINNET_CHAIN_ID,
  ARBITRUM_SEPOLIA_TESTNET_CHAIN_ID,
  OPTIMISM_MAINNET_CHAIN_ID,
  OPTIMISM_SEPOLIA_TESTNET_CHAIN_ID,
  BASE_MAINNET_CHAIN_ID,
  BASE_SEPOLIA_TESTNET_CHAIN_ID,
  SCROLL_MAINNET_CHAIN_ID,
  SCROLL_SEPOLIA_TESTNET_CHAIN_ID,
  ZKSYNC_ERA_MAINNET_CHAIN_ID,
  ZKSYNC_ERA_SEPOLIA_TESTNET_CHAIN_ID,
  POLYGON_ZKEVM_MAINNET_CHAIN_ID,
  POLYGON_ZKEVM_CARDONA_TESTNET_CHAIN_ID,
  CELO_MAINNET_CHAIN_ID,
  CELO_ALFAJORES_TESTNET_CHAIN_ID,
  GNOSIS_MAINNET_CHAIN_ID,
  GNOSIS_CHIADO_TESTNET_CHAIN_ID,
  MANTLE_MAINNET_CHAIN_ID,
  MANTLE_SEPOLIA_TESTNET_CHAIN_ID,
  BLAST_MAINNET_CHAIN_ID,
  BLAST_SEPOLIA_TESTNET_CHAIN_ID,
];
